import { HOUSEHOLD_TIMEZONE } from '../enums.js';
import { expandOccurrences, localDateKey } from './recurrence.js';
/**
 * "The week is the interface" (docs/AGENDA.md §1).
 *
 * The pure core: which seven days a reference instant belongs to, how the stored
 * rows expand into per-day occurrences, and — the heart of the module — whether,
 * at every moment, we know who has the child. Both the client and the server run
 * this, so a badge on a phone and a notification from the worker are computed
 * from the same rules.
 *
 * Two timelines, one question:
 *   - **activities** — what Jurren and Olga do, never an external person;
 *   - **childcare** — an explicit caregiver for a window (a parent or an
 *     external person); a null end means the whole day.
 * A moment is **covered** when a caregiver explicitly covers it — nobody is ever
 * assumed to have the child just because their calendar happens to be empty
 * (docs §5, 2026-08-19 feedback); otherwise it is **uncovered**. Symmetrically, a
 * parent who is down as the caregiver for a window and also has an activity in
 * it is flagged the same way two overlapping activities are — they can't be in
 * both places.
 */
const MS_PER_DAY = 86_400_000;
/* ---- date-key arithmetic, anchored at UTC noon so DST never shifts a day ---- */
function keyToNoonMs(key) {
    return Date.parse(`${key}T12:00:00Z`);
}
function noonMsToKey(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
function addDaysKey(key, days) {
    return noonMsToKey(keyToNoonMs(key) + days * MS_PER_DAY);
}
/** ISO weekday, 1 = Monday … 7 = Sunday. */
function weekdayOfKey(key) {
    const d = new Date(`${key}T12:00:00Z`).getUTCDay();
    return d === 0 ? 7 : d;
}
/**
 * The tz offset in minutes at `ms` (local wall clock minus UTC). Used only to
 * find local midnight for the multi-day split below; the household is
 * single-timezone, and the same DST imprecision `expandOccurrences` already
 * accepts applies here — a boundary an hour off twice a year never changes which
 * day a segment lands on.
 */
function tzOffsetMinutes(ms, tz) {
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const p = {};
    for (const part of dtf.formatToParts(new Date(ms)))
        if (part.type !== 'literal')
            p[part.type] = part.value;
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return (asUtc - ms) / 60_000;
}
/** The instant local midnight begins on `key` in `tz`. */
function localDayStartMs(key, tz) {
    const utcMidnight = Date.parse(`${key}T00:00:00Z`);
    return utcMidnight - tzOffsetMinutes(utcMidnight, tz) * 60_000;
}
/** Monday's local date key for the week containing `refIso`. */
export function weekStart(refIso, tz = HOUSEHOLD_TIMEZONE) {
    const refKey = localDateKey(Date.parse(refIso), tz);
    return addDaysKey(refKey, -(weekdayOfKey(refKey) - 1));
}
/** The seven local date keys of the week, Monday first. */
export function weekDays(refIso, tz = HOUSEHOLD_TIMEZONE) {
    const monday = weekStart(refIso, tz);
    return Array.from({ length: 7 }, (_, i) => addDaysKey(monday, i));
}
/** Move the reference instant by whole weeks — the ‹ › navigation. */
export function shiftWeeks(refIso, weeks) {
    return new Date(Date.parse(refIso) + weeks * 7 * MS_PER_DAY).toISOString();
}
/**
 * A generous instant window that certainly covers the local week (padded a day
 * each side, since local midnight in Amsterdam is an hour or two before UTC
 * midnight). Occurrences are bucketed by their local date afterwards, so the
 * padding never leaks a neighbouring day onto the screen.
 */
export function weekRangeInstants(refIso, tz = HOUSEHOLD_TIMEZONE) {
    const days = weekDays(refIso, tz);
    return {
        start: `${addDaysKey(days[0], -1)}T00:00:00.000Z`,
        end: `${addDaysKey(days[6], 2)}T00:00:00.000Z`,
    };
}
function mergeIntervals(ivs) {
    const sorted = ivs.filter(([a, b]) => b > a).sort((x, y) => x[0] - y[0]);
    const out = [];
    for (const iv of sorted) {
        const last = out[out.length - 1];
        if (last && iv[0] <= last[1])
            last[1] = Math.max(last[1], iv[1]);
        else
            out.push([iv[0], iv[1]]);
    }
    return out;
}
/** The parts of `target` that none of `covers` cover. */
function subtractIntervals(target, covers) {
    const out = [];
    let cur = target[0];
    for (const [cs, ce] of mergeIntervals(covers)) {
        if (ce <= target[0] || cs >= target[1])
            continue;
        if (cs > cur)
            out.push([cur, Math.min(cs, target[1])]);
        cur = Math.max(cur, ce);
        if (cur >= target[1])
            break;
    }
    if (cur < target[1])
        out.push([cur, target[1]]);
    return out.filter(([a, b]) => b > a);
}
/**
 * Split one occurrence into per-day pieces (2026-08-20 feedback: events may span
 * days). A same-day occurrence is one piece — the common case, unchanged. A
 * span is clipped at each local midnight, so every day it touches gets its own
 * `WeekItem` (`activity:${id}:${day}` stays unique per day) and per-day coverage
 * keeps working with no special case. An `ends_at` exactly on midnight belongs
 * to the day before (an exclusive end), so a 09:00→next-midnight event is one
 * day, not two.
 */
function daySegments(startsAt, endsAt, tz) {
    const startMs = Date.parse(startsAt);
    const startKey = localDateKey(startMs, tz);
    if (!endsAt)
        return [{ day: startKey, starts_at: startsAt, ends_at: null, continuesBefore: false, continuesAfter: false }];
    const endMs = Date.parse(endsAt);
    let endKey = localDateKey(endMs, tz);
    if (localDayStartMs(endKey, tz) === endMs)
        endKey = addDaysKey(endKey, -1);
    if (endKey <= startKey) {
        return [{ day: startKey, starts_at: startsAt, ends_at: endsAt, continuesBefore: false, continuesAfter: false }];
    }
    const segs = [];
    for (let key = startKey; key <= endKey && segs.length < 60; key = addDaysKey(key, 1)) {
        const isFirst = key === startKey;
        const isLast = key === endKey;
        const s = isFirst ? startMs : localDayStartMs(key, tz);
        const e = isLast ? endMs : localDayStartMs(addDaysKey(key, 1), tz);
        segs.push({
            day: key,
            starts_at: new Date(s).toISOString(),
            ends_at: new Date(e).toISOString(),
            continuesBefore: !isFirst,
            continuesAfter: !isLast,
        });
    }
    return segs;
}
const notDeleted = (r) => !r.deleted_at;
/** Expand every stored row into the concrete occurrences that fall in the week. */
export function expandWeek(rows, refIso, tz = HOUSEHOLD_TIMEZONE) {
    const days = weekDays(refIso, tz);
    const daySet = new Set(days);
    const { start, end } = weekRangeInstants(refIso, tz);
    const items = [];
    for (const act of (rows.activities ?? []).filter(notDeleted)) {
        const personKeys = (act.participants ?? []).map((p) => `${p.kind}:${p.id}`);
        for (const occ of expandOccurrences(act, start, end, tz)) {
            for (const seg of daySegments(occ.starts_at, occ.ends_at, tz)) {
                if (!daySet.has(seg.day))
                    continue;
                items.push({
                    key: `activity:${act.id}:${seg.day}`,
                    rowId: act.id,
                    type: 'activity',
                    title: act.title,
                    starts_at: seg.starts_at,
                    ends_at: seg.ends_at,
                    all_day: act.all_day ?? false,
                    day: seg.day,
                    personKeys,
                    needsCar: act.needs_car ?? false,
                    bringsChild: act.brings_child ?? false,
                    continuesBefore: seg.continuesBefore,
                    continuesAfter: seg.continuesAfter,
                    note: act.notes ?? null,
                });
            }
        }
    }
    for (const cc of (rows.childcare ?? []).filter(notDeleted)) {
        const caregiverKeys = (cc.caregivers ?? []).map((p) => `${p.kind}:${p.id}`);
        for (const occ of expandOccurrences(cc, start, end, tz)) {
            for (const seg of daySegments(occ.starts_at, occ.ends_at, tz)) {
                if (!daySet.has(seg.day))
                    continue;
                const wholeDay = !seg.ends_at;
                items.push({
                    key: `childcare:${cc.id}:${seg.day}`,
                    rowId: cc.id,
                    type: 'childcare',
                    title: '',
                    starts_at: seg.starts_at,
                    ends_at: seg.ends_at,
                    all_day: wholeDay,
                    day: seg.day,
                    personKeys: caregiverKeys,
                    caregiverKeys,
                    continuesBefore: seg.continuesBefore,
                    continuesAfter: seg.continuesAfter,
                    note: cc.notes ?? null,
                });
            }
        }
    }
    return { weekStart: days[0], days, items, coverage: computeCoverage(items, days) };
}
function emptyDay() {
    return { conflicts: 0, carConflicts: 0, uncovered: 0 };
}
function effEnd(startIso, endIso) {
    const s = Date.parse(startIso);
    const e = endIso ? Date.parse(endIso) : s;
    return e > s ? e : s;
}
function overlaps(a, b) {
    const aS = Date.parse(a.starts_at);
    const bS = Date.parse(b.starts_at);
    return aS < effEnd(b.starts_at, b.ends_at) && bS < effEnd(a.starts_at, a.ends_at);
}
function sharePerson(a, b) {
    return a.personKeys.some((k) => b.personKeys.includes(k));
}
/**
 * The interval an activity *requires* coverage for. A timed activity needs its
 * span covered; an all-day activity carries no minute-level requirement (target
 * null → treated as covered), the long-standing behaviour.
 */
function coverIv(item) {
    // A segment that continues from the previous day is the tail of an occurrence
    // already judged on the day it started, so it carries no requirement of its
    // own. Without this, a party running to 01:00 is split at midnight and the
    // 00:00–01:00 tail is evaluated as a fresh activity with nobody down for the
    // child — inventing a red "waar is de kleine tussen 00:00–01:00?" for an
    // evening that was covered, and a warning dot on the following day.
    if (item.continuesBefore)
        return null;
    const s = Date.parse(item.starts_at);
    const e = item.ends_at ? Date.parse(item.ends_at) : s;
    return e > s ? [s, e] : null;
}
/**
 * The interval an item *provides* coverage over. A childcare window covers its
 * span (a whole-day window covers its day generously); a `brings_child` activity
 * covers the child for the parent's own span, so the activity needs no separate
 * opvang row (2026-08-20 feedback). Whose coverage it is: the childcare's
 * caregivers, or the activity's participants.
 */
function provideIv(item) {
    if (item.type === 'childcare' && (item.all_day || !item.ends_at)) {
        const noon = keyToNoonMs(item.day);
        return [noon - MS_PER_DAY, noon + MS_PER_DAY];
    }
    const s = Date.parse(item.starts_at);
    const e = item.ends_at ? Date.parse(item.ends_at) : s;
    return e > s ? [s, e] : null;
}
/**
 * From the week's items: who has the child at every moment, what clashes, what
 * still has nobody down for it. Coverage answers to one rule only — an explicit
 * childcare row covering the moment — never an assumption that whoever's
 * calendar looks empty must have the child (docs §5, 2026-08-19 feedback).
 */
export function computeCoverage(items, days) {
    const dayList = days ?? [...new Set(items.map((i) => i.day))].sort();
    const summary = {};
    for (const d of dayList)
        summary[d] = emptyDay();
    const ensure = (d) => (summary[d] ??= emptyDay());
    const activities = items.filter((i) => i.type === 'activity');
    const childcare = items.filter((i) => i.type === 'childcare');
    /* ---- conflicts: a person double-booked — two overlapping activities, or an
       overlapping activity while they are the assigned childcare caregiver ---- */
    const conflict = new Set();
    for (let i = 0; i < activities.length; i += 1) {
        for (let j = i + 1; j < activities.length; j += 1) {
            const a = activities[i];
            const b = activities[j];
            if (a.day !== b.day || !sharePerson(a, b) || !overlaps(a, b))
                continue;
            conflict.add(a.key);
            conflict.add(b.key);
        }
    }
    for (const a of activities) {
        for (const c of childcare) {
            if (a.day !== c.day || !sharePerson(a, c) || !overlaps(a, c))
                continue;
            conflict.add(a.key);
            conflict.add(c.key);
        }
    }
    /* ---- car: two activities needing the car at once ---- */
    const carConflict = new Set();
    const carActs = activities.filter((a) => a.needsCar);
    for (let i = 0; i < carActs.length; i += 1) {
        for (let j = i + 1; j < carActs.length; j += 1) {
            const a = carActs[i];
            const b = carActs[j];
            if (a.day !== b.day || !overlaps(a, b))
                continue;
            carConflict.add(a.key);
            carConflict.add(b.key);
        }
    }
    /* ---- childcare coverage, per activity: only an explicit caregiver counts ---- */
    const activityCoverage = {};
    const uncoveredByDay = {};
    // A day's coverage sources: every childcare window, plus every activity that
    // takes the child along — a brings_child activity covers the child for its
    // participants over its own span (2026-08-20 feedback).
    const caregiverByDay = new Map();
    const addSource = (day, iv, keys) => {
        if (!iv || keys.length === 0)
            return;
        (caregiverByDay.get(day) ?? caregiverByDay.set(day, []).get(day)).push({ iv, keys });
    };
    for (const c of childcare)
        addSource(c.day, provideIv(c), c.caregiverKeys ?? c.personKeys);
    for (const a of activities)
        if (a.bringsChild)
            addSource(a.day, provideIv(a), a.personKeys);
    for (const a of activities) {
        const target = coverIv(a);
        if (!target) {
            activityCoverage[a.key] = { state: 'covered', carerKeys: [], open: [] };
            continue;
        }
        const dayCaregivers = caregiverByDay.get(a.day) ?? [];
        const open = subtractIntervals(target, dayCaregivers.map((c) => c.iv));
        const carerKeys = [
            ...new Set(dayCaregivers.filter((c) => c.iv[0] < target[1] && c.iv[1] > target[0]).flatMap((c) => c.keys)),
        ];
        const state = open.length === 0 ? 'covered' : open.length === 1 && open[0][0] === target[0] && open[0][1] === target[1] ? 'uncovered' : 'partial';
        activityCoverage[a.key] = {
            state,
            carerKeys,
            open: open.map(([s, e]) => ({ starts_at: new Date(s).toISOString(), ends_at: new Date(e).toISOString() })),
        };
        if (state !== 'covered') {
            ensure(a.day).uncovered += 1;
            const list = (uncoveredByDay[a.day] ??= []);
            for (const [s, e] of open)
                list.push([s, e]);
        }
    }
    // Merge each day's open windows so overlapping gaps from two activities read as one.
    for (const day of Object.keys(uncoveredByDay)) {
        const merged = mergeIntervals(uncoveredByDay[day]);
        uncoveredByDay[day] = merged.map(([s, e]) => ({ starts_at: new Date(s).toISOString(), ends_at: new Date(e).toISOString() }));
    }
    /**
     * The same coverage sources, clipped to their own day, for a timeline view.
     *
     * A whole-day childcare window provides a deliberately generous ±24h interval
     * (`provideIv`) so that coverage maths never trips over a boundary; drawn
     * literally that would be a bar three days wide, so it is clipped here. Noon
     * ±12h rather than true local midnights: this is a display extent, the gap
     * instants it sits beside are exact, and a DST day being an hour out of true
     * is invisible at bar width.
     */
    const coveredByDay = {};
    for (const [day, sources] of caregiverByDay) {
        const noon = keyToNoonMs(day);
        const [dayStart, dayEnd] = [noon - MS_PER_DAY / 2, noon + MS_PER_DAY / 2];
        const windows = sources
            .map(({ iv, keys }) => [Math.max(iv[0], dayStart), Math.min(iv[1], dayEnd), keys])
            .filter(([s, e]) => e > s)
            .sort((a, b) => a[0] - b[0])
            .map(([s, e, keys]) => ({
            starts_at: new Date(s).toISOString(),
            ends_at: new Date(e).toISOString(),
            carerKeys: keys,
        }));
        if (windows.length > 0)
            coveredByDay[day] = windows;
    }
    /* ---- tallies ---- */
    for (const a of activities)
        if (conflict.has(a.key))
            ensure(a.day).conflicts += 1;
    for (const c of childcare)
        if (conflict.has(c.key))
            ensure(c.day).conflicts += 1;
    for (const a of activities)
        if (carConflict.has(a.key))
            ensure(a.day).carConflicts += 1;
    const totals = emptyDay();
    for (const d of Object.values(summary)) {
        totals.conflicts += d.conflicts;
        totals.carConflicts += d.carConflicts;
        totals.uncovered += d.uncovered;
    }
    return {
        days: summary,
        totals,
        conflictKeys: [...conflict],
        carConflictKeys: [...carConflict],
        activityCoverage,
        uncoveredByDay,
        coveredByDay,
    };
}
//# sourceMappingURL=week.js.map